import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { usePageTransition } from "@/hooks/usePageTransition";
import IntakeTransitionOverlay from "@/components/feature/IntakeTransitionOverlay";
import IntakeProgressBar from "@/components/feature/IntakeProgressBar";
import { complianceFooterText } from "@/mocks/intake-pages";
import {
  STAGE1_FIELD_BINDINGS,
  getBlockPosition,
  getQuestionsForBlock,
  getTotalIntakeSteps,
  getNextBlockId,
  getPreviousBlockId,
  isBlockComplete,
  loadStage1Answers,
  saveStage1Answers,
  type Stage1Answers,
  type Stage1FieldBinding,
  type Stage1Question,
} from "@/lib/stage1Questionnaire";

interface QuestionFieldProps {
  question: Stage1Question;
  binding: Stage1FieldBinding;
  answers: Stage1Answers;
  isBilateral: boolean;
  setField: (patch: Partial<Stage1Answers>) => void;
}

function QuestionField({
  question,
  binding,
  answers,
  isBilateral,
  setField,
}: QuestionFieldProps) {
  const mainValue = String(answers[binding.field] ?? "");
  const selectedValues = (answers[binding.field] as string[]) ?? [];

  const displayQuestionText =
    question.bilateralPairLabel && isBilateral
      ? `${question.question} (${question.bilateralPairLabel})`
      : question.question;

  const handleSingleSelect = (raw: string) => {
    setField({
      [binding.field]: binding.numeric ? Number(raw) : raw,
    } as Partial<Stage1Answers>);
  };

  const handleMultiSelectToggle = (raw: string) => {
    let next: string[];
    if (raw === "none") {
      next = selectedValues.includes("none") ? [] : ["none"];
    } else {
      const withoutNone = selectedValues.filter((v) => v !== "none");
      next = withoutNone.includes(raw)
        ? withoutNone.filter((v) => v !== raw)
        : [...withoutNone, raw];
    }
    setField({ [binding.field]: next } as Partial<Stage1Answers>);
  };

  const renderOptionButtons = (
    onSelect: (value: string) => void,
    isSelected: (value: string) => boolean,
  ) => (
    <div className="space-y-3 mt-4">
      {question.options?.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={`w-full text-left px-5 py-4 rounded-xl border-2 text-sm md:text-base font-medium transition-all cursor-pointer ${
            isSelected(opt.value)
              ? "bg-primary-500 text-background-50 border-primary-500 shadow-sm"
              : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:bg-primary-50"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const renderInput = () => {
    switch (question.inputType) {
      case "single-select":
      case "yes-no":
        return renderOptionButtons(
          handleSingleSelect,
          (value) => mainValue === value,
        );

      case "multi-select":
        return renderOptionButtons(handleMultiSelectToggle, (value) =>
          selectedValues.includes(value),
        );

      case "single-select-with-text":
      case "single-select-with-conditional-text": {
        const showText =
          question.inputType === "single-select-with-text" ||
          mainValue === (question.conditionalTextTrigger ?? "yes");
        return (
          <>
            {renderOptionButtons(
              handleSingleSelect,
              (value) => mainValue === value,
            )}
            {showText && binding.detailField && (
              <div className="mt-5 animate-fadeSlideIn">
                <label className="block text-xs font-semibold text-foreground-500 mb-2">
                  Tell us a bit more (optional):
                </label>
                <textarea
                  value={String(answers[binding.detailField] ?? "")}
                  onChange={(e) =>
                    setField({
                      [binding.detailField as string]: e.target.value,
                    } as Partial<Stage1Answers>)
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
            )}
          </>
        );
      }

      case "yes-no-with-slider": {
        const severity = binding.severityField
          ? ((answers[binding.severityField] as number | null) ?? 0)
          : 0;
        return (
          <>
            {renderOptionButtons(
              handleSingleSelect,
              (value) => mainValue === value,
            )}
            {mainValue === "yes" && binding.severityField && (
              <div className="mt-5 animate-fadeSlideIn">
                <label className="block text-xs font-semibold text-foreground-500 mb-2">
                  How severe on average? (0 = none, 10 = worst)
                </label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={severity}
                  onChange={(e) =>
                    setField({
                      [binding.severityField as string]: Number(e.target.value),
                    } as Partial<Stage1Answers>)
                  }
                  className="w-full cursor-pointer"
                />
                <div className="text-center text-sm font-bold text-primary-600 mt-1">
                  {severity}
                </div>
              </div>
            )}
          </>
        );
      }

      case "slider": {
        const value = (answers[binding.field] as number | null) ?? 0;
        return (
          <div className="mt-4">
            <input
              type="range"
              min={0}
              max={10}
              value={value}
              onChange={(e) => handleSingleSelect(e.target.value)}
              className="w-full cursor-pointer"
            />
            <div className="text-center text-sm font-bold text-primary-600 mt-1">
              {value}
            </div>
          </div>
        );
      }

      case "date":
        return (
          <div className="mt-4">
            <input
              type="date"
              value={mainValue}
              onChange={(e) => handleSingleSelect(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="py-6 first:pt-0 border-b border-background-100 last:border-b-0">
      <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-1">
        {displayQuestionText}
      </h2>
      {renderInput()}
    </div>
  );
}

export default function IntakeStep() {
  const { blockId = "" } = useParams<{ blockId: string }>();
  const { goTo, containerClasses } = usePageTransition();
  const [answers, setAnswers] = useState<Stage1Answers>(() =>
    loadStage1Answers(),
  );
  const [transitioning, setTransitioning] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTransitioning(false);
    // IntakeStep stays mounted across /intake/step/:blockId navigations (same route
    // element, only the param changes), so the scrollable area keeps its old offset
    // unless we reset it — otherwise the new block renders mid-scroll and its first
    // questions appear "missing" above the fold.
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [blockId]);

  const questions = getQuestionsForBlock(blockId, answers);

  if (getBlockPosition(blockId) < 0 || questions.length === 0) {
    return <Navigate to="/intake" replace />;
  }

  const setField = (patch: Partial<Stage1Answers>) => {
    setAnswers((prev) => {
      const next = { ...prev, ...patch };
      saveStage1Answers(next);
      return next;
    });
  };

  const currentStep = getBlockPosition(blockId) + 2;
  const totalSteps = getTotalIntakeSteps();
  const prevBlockId = getPreviousBlockId(blockId);
  const backHref = prevBlockId ? `/intake/step/${prevBlockId}` : "/intake";
  const proceedEnabled = isBlockComplete(blockId, answers) && !transitioning;
  const isBilateral = answers.p2AmputationSide === "bilateral";

  const handleProceed = () => {
    if (!proceedEnabled) return;
    setTransitioning(true);
    const nextBlockId = getNextBlockId(blockId);
    setTimeout(() => {
      goTo(
        nextBlockId
          ? `/intake/step/${nextBlockId}`
          : "/intake/klevel-blueprint",
      );
    }, 400);
  };

  return (
    <div className="h-screen flex flex-col bg-background-50">
      <IntakeTransitionOverlay />
      <div className="flex-shrink-0 bg-white border-b border-background-100">
        <div className="max-w-[720px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-4">
            <Link
              to={backHref}
              className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back
            </Link>
          </div>
          <div className="pb-4">
            <IntakeProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
            />
          </div>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className={`flex-1 flex items-start justify-center overflow-y-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-500 ease-out relative ${containerClasses}`}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-100/20 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-primary-100/12 blur-3xl"></div>
        </div>

        <div className="w-full max-w-[720px] mx-auto relative z-10">
          <div className="bg-white rounded-2xl border border-background-100 p-8 md:p-10 animate-fadeSlideIn">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-800 mb-4">
              {questions[0].block}
            </span>

            <div>
              {questions.map((question) => {
                const binding = STAGE1_FIELD_BINDINGS[question.id];
                if (!binding) return null;
                return (
                  <QuestionField
                    key={question.id}
                    question={question}
                    binding={binding}
                    answers={answers}
                    isBilateral={isBilateral}
                    setField={setField}
                  />
                );
              })}
            </div>

            <div className="mt-8">
              <button
                type="button"
                onClick={handleProceed}
                disabled={!proceedEnabled}
                className={`w-full py-3.5 rounded-xl text-sm md:text-base font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  proceedEnabled
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-background-200 text-foreground-400 cursor-not-allowed"
                }`}
              >
                {transitioning ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line text-sm animate-spin"></i>
                    </span>
                    Proceeding...
                  </>
                ) : (
                  "Proceed"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-t border-background-100 bg-background-50">
        <div className="max-w-[720px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-400 mt-0.5">
              <i className="ri-information-line text-[10px]"></i>
            </span>
            <p className="text-[10px] text-foreground-400 leading-relaxed max-w-3xl">
              {complianceFooterText}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
